import { useEffect } from "react";
import { useReactFlow, useStore } from "@xyflow/react";
import { CV } from "@/config/canvasSizing";

// Hand-tool click-to-focus camera. Rendered *inside* <ReactFlow> (Viewport's root
// is outside the RF context). When the pan tool clicks a node, this zooms in and
// centres that node — the same motion the pages-panel badge uses (PanelCamera),
// but centred since there's no panel to dodge and without opening the drawer.
// focusTick re-fires the effect when the same node is clicked again. Null-rendering.

interface Props {
  focusNodeId: string | null;
  focusTick: number;
}

export default function HandFocusCamera({ focusNodeId, focusTick }: Props) {
  const rf = useReactFlow();
  const vw = useStore((s) => s.width);
  const vh = useStore((s) => s.height);

  useEffect(() => {
    if (!focusNodeId || !vw || !vh) return;
    const node = rf.getNode(focusNodeId);
    if (!node) return;

    const w = node.width ?? node.measured?.width ?? CV.node.defaultW;
    const h = node.height ?? node.measured?.height ?? CV.node.defaultH;
    const cx = node.position.x + w / 2;
    const cy = node.position.y + h / 2;

    const { zoom, durationMs } = CV.pagesPanel.camera;
    // Centre the node in the viewport: screen = flow·zoom + translate.
    void rf.setViewport(
      { x: vw / 2 - cx * zoom, y: vh / 2 - cy * zoom, zoom },
      { duration: durationMs },
    );
    // focusTick is intentionally a dep so re-clicking the same node re-centres.
  }, [focusNodeId, focusTick, vw, vh, rf]);

  return null;
}
